import type { Department } from "../../contexts/AuthProvider";
import type { Nurse, NurseInShift } from "../../utils/models";
import { shouldApplyNurseNotOnboardedVisibleFlag } from "../../utils/utilFunctions";
import { type AiPrintoutCounterparts, shiftTimesOf } from "./aiPrintoutCounterparts";
import type { AiPrintoutShiftHppdFields } from "./aiPrintoutMetrics";
import type { AiPrintoutShiftFactKey, AiPrintoutTimeContext } from "./aiPrintoutRowMappers";
import type { TimeGroupKeysOf } from "./aiPrintoutTimeGroups";
import type { AiPrintoutShift } from "./contract";

interface CreateShiftFactsArgs {
	isActual: boolean;
	counterparts: AiPrintoutCounterparts;
	timeGroupKeysOf: TimeGroupKeysOf;
	hppdFieldsOf: (shift: NurseInShift, isActual: boolean) => AiPrintoutShiftHppdFields;
	nurses: Nurse[];
	department: Department;
	jobManagementEnabled: boolean;
	context: AiPrintoutTimeContext;
}

// A scheduled shift standing in for a punch is not in the punches the day's actual totals are computed over.
const STAND_IN_HPPD_FIELDS = { countsTowardHppd: false, countsTowardDhppd: false, countsTowardNhppd: false, countsTowardPpd: false };

/**
 * A shown shift's facts from the rest of the day: its other side, its time groups, how the HPPD and PPD totals
 * count it, and the Detailed printout's "Not onboarded" flag (`shouldApplyNurseNotOnboardedVisibleFlag`, read
 * off the roster as `buildNurseNotes` reads it).
 */
export const createShiftFacts = (args: CreateShiftFactsArgs): ((shift: NurseInShift) => Pick<AiPrintoutShift, AiPrintoutShiftFactKey>) => {
	const { isActual, counterparts, department, jobManagementEnabled, context } = args;
	const nurseById = new Map(args.nurses.map((nurse) => [nurse.id, nurse]));
	const timesOf = (shift: NurseInShift) => shiftTimesOf({ shift, isStandIn: counterparts.isStandIn(shift), department, jobManagementEnabled, context });
	return (shift) => {
		const counterpart = counterparts.counterpartOf(shift);
		const own = timesOf(shift);
		const other = counterpart === undefined ? null : timesOf(counterpart);
		return {
			notOnboarded: shouldApplyNurseNotOnboardedVisibleFlag(nurseById.get(shift.nurseId)),
			scheduled: isActual ? other : own,
			actual: isActual ? own : other,
			timeGroupKeys: args.timeGroupKeysOf(shift.startTime, shift.endTime),
			...args.hppdFieldsOf(shift, isActual),
			...(own.isStandIn ? STAND_IN_HPPD_FIELDS : {}),
		};
	};
};
